import type { AiModel, AiModelPayload, AiModelsListResponse } from '@/types';

import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { apiClient } from '@/utils/api';
import { buildApiParams } from '@/utils/helpers';
import { createPaginationParams, createTableQueryOptions } from '@/components/table';

const AI_MODELS_BASE = '/ai-models';

export const AI_MODELS_DEFAULT_PER_PAGE = 25;

export type AiModelsListParams = {
  page: number;
  per_page: number;
  filters: Record<string, string | undefined>;
  sort?: string;
  refresh?: number;
};

export const aiModelsListParamsAtom = atom<AiModelsListParams>({
  ...createPaginationParams(1, AI_MODELS_DEFAULT_PER_PAGE),
  filters: {}
});

export const aiModelsHydratedAtom = atom<boolean>(false);

export const aiModelsQueryAtom = atomWithQuery<AiModelsListResponse>((get) => {
  const params = get(aiModelsListParamsAtom);
  const hydrated = get(aiModelsHydratedAtom);

  return createTableQueryOptions({
    queryKey: ['ai-models', params] as const,
    queryFn: async () => {
      const apiParams = buildApiParams(
        {
          page: params.page,
          sort: params.sort
        },
        params.filters || {}
      );
      apiParams.per_page = params.per_page;

      const { data } = await apiClient.get(AI_MODELS_BASE, { params: apiParams });
      return data as AiModelsListResponse;
    },
    enabled: hydrated
  });
});

const touchAiModelsList = (get: any, set: any) => {
  const currentParams = get(aiModelsListParamsAtom);
  set(aiModelsListParamsAtom, { ...currentParams, refresh: Date.now() });
};

export const syncAiModelsAtom = atom(null, async (get, set) => {
  await apiClient.post(`${AI_MODELS_BASE}/sync`);
  touchAiModelsList(get, set);
});

export const reorderAiModelsAtom = atom(null, async (get, set, ids: number[]) => {
  await apiClient.put(`${AI_MODELS_BASE}/reorder`, { ids });
  touchAiModelsList(get, set);
});

export const updateAiModelAtom = atom(
  null,
  async (get, set, { id, payload }: { id: number; payload: Partial<AiModelPayload> }) => {
    await apiClient.put(`${AI_MODELS_BASE}/${id}`, payload);
    touchAiModelsList(get, set);
  }
);

export const createAiModelAtom = atom(null, async (get, set, payload: AiModelPayload) => {
  await apiClient.post(AI_MODELS_BASE, payload);
  touchAiModelsList(get, set);
});

export const deleteAiModelAtom = atom(null, async (get, set, id: number) => {
  await apiClient.delete(`${AI_MODELS_BASE}/${id}`);
  touchAiModelsList(get, set);
});

export const latencyTestAiModelAtom = atom(null, async (_get, _set, id: number) => {
  const { data } = await apiClient.post(`${AI_MODELS_BASE}/${id}/latency-test`);
  return data;
});

export async function fetchAllAiModelIds(): Promise<number[]> {
  const { data } = await apiClient.get<AiModelsListResponse>(AI_MODELS_BASE, {
    params: { per_page: 10000, sort: 'sort_order' }
  });

  return (data.data ?? []).map((model) => model.id);
}

export type OrderedAiModel = AiModel;
